import axios from 'axios';
import ApiConfig from 'src/config/api-config';
import type { SectionTimeData } from 'src/utils/screenTimeTracker';

// ------------------------------------------------------------
// Types
// ------------------------------------------------------------

export interface UserBehaviorPayload {
  /**
   * The session UUID v4 returned by the backend on the first call.
   * Omit on the very first request – the backend will generate one
   * and return it in the response. Include on all subsequent requests
   * so the backend can upsert the same Firestore document.
   */
  sessionId?: string;
  /**
   * User email. Pass an empty string `""` when the user has not
   * yet provided one (e.g. on the Generate click).
   */
  email: string;
  /** Screen-time report from ScreenTimeTracker.getReport(). */
  data: SectionTimeData[];
}

export interface UserBehaviorResponse {
  status: string;
  /** The session ID for this record – either the one you sent or the
   *  backend-generated UUID v4 when no sessionId was provided. */
  sessionId: string;
  message: string;
}

// ------------------------------------------------------------
// API call – plain async function (called imperatively, not via a hook)
// ------------------------------------------------------------

/**
 * Posts screen-time analytics to the Firebase Cloud Function.
 *
 * Returns the full response so the caller can persist the backend-assigned
 * `sessionId` after the first (anonymous) call.
 *
 * This is intentionally a plain async function (not a React mutation hook)
 * so it can be called as fire-and-forget from any button handler without
 * coupling the caller to React Query state.
 *
 * Errors are propagated to the caller – wrap in try/catch / .catch() when
 * you need silent failure.
 */
export const trackUserBehavior = async (
  payload: UserBehaviorPayload,
): Promise<UserBehaviorResponse> => {
  const response = await axios.post<UserBehaviorResponse>(
    `${ApiConfig.CLOUD_FUNCTIONS_BASE_URL}${ApiConfig.USER_BEHAVIOR_TRACKER_API_HOST}`,
    payload,
    {
      headers: { 'Content-Type': 'application/json' },
      // Allow up to 10 s for the Cloud Function cold start
      timeout: 10000,
    },
  );
  return response.data;
};
